import React from "react";
import { Link } from "react-router-dom";

/**
 * HomeHeroPreview — 3 fixes for the baby's-head-cut issue.
 * Live at /home-hero-preview
 */

const Section = ({ id, kicker, title, note, children }) => (
  <section id={id} className="hairline-b" data-testid={`hero-fix-${id}`}>
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-10">
      <div className="grid md:grid-cols-12 gap-6 items-end mb-6">
        <div className="md:col-span-8">
          <div className="nav-item mb-2" style={{ color: "var(--accent)" }}>{kicker}</div>
          <h2 className="serif text-3xl md:text-5xl leading-[1.05]">{title}</h2>
        </div>
        <p className="md:col-span-4 text-sm text-ink/70 leading-relaxed">{note}</p>
      </div>
      {children}
    </div>
  </section>
);

const IMG = "/api/uploads/home/hero-mockup.webp";

/* A · Transparent header over hero — no cut, ivory nav text */
const VariantTransparent = () => (
  <div className="relative w-full" style={{ background: "#1c1512" }}>
    {/* fake site header overlay showing what it would look like */}
    <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 md:px-12 py-5" style={{ background: "linear-gradient(180deg, rgba(20,15,12,0.55) 0%, rgba(20,15,12,0) 100%)" }}>
      <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase" style={{ color: "#F6F1E9" }}>
        <span>Shop</span><span>The Craft</span>
      </div>
      <div className="serif italic text-2xl" style={{ color: "#F6F1E9" }}>MELAVI</div>
      <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase" style={{ color: "#F6F1E9" }}>
        <span>About</span><span>Contact</span><span>Cart 0</span>
      </div>
    </div>
    <img src={IMG} alt="" className="w-full h-auto block" />
  </div>
);

/* B · Sink the hero — hero image starts BELOW the site header */
const VariantSink = () => (
  <div className="relative w-full" style={{ background: "#1c1512" }}>
    <div className="w-full h-24" style={{ background: "#F6F1E9" }}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between h-full">
        <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase text-ink">
          <span>Shop</span><span>The Craft</span>
        </div>
        <div className="serif italic text-2xl">MELAVI</div>
        <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase text-ink">
          <span>About</span><span>Contact</span><span>Cart 0</span>
        </div>
      </div>
    </div>
    <img src={IMG} alt="" className="w-full h-auto block" />
  </div>
);

/* C · Extend the top of the image with a matching dark gradient headroom */
const VariantHeadroom = () => (
  <div className="relative w-full" style={{ background: "#1c1512" }}>
    <div className="relative" style={{ paddingTop: "80px", background: "linear-gradient(180deg, #1c1512 0%, #241a15 60%, #2a1e18 100%)" }}>
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase" style={{ color: "rgba(246,241,233,0.85)" }}>
          <span>Shop</span><span>The Craft</span>
        </div>
        <div className="serif italic text-2xl" style={{ color: "#F6F1E9" }}>MELAVI</div>
        <div className="flex gap-8 text-[11px] tracking-[0.32em] uppercase" style={{ color: "rgba(246,241,233,0.85)" }}>
          <span>About</span><span>Contact</span><span>Cart 0</span>
        </div>
      </div>
      <img src={IMG} alt="" className="w-full h-auto block" />
    </div>
  </div>
);

const HomeHeroPreview = () => (
  <div data-testid="home-hero-preview" style={{ background: "var(--ground)" }}>
    <Section
      id="a"
      kicker="A · Transparent header"
      title="Header fades to glass over the hero."
      note="Nav sits directly over the image with a subtle top-to-transparent scrim + ivory text. Zero image crop. Most cinematic — full baby visible."
    >
      <VariantTransparent />
    </Section>

    <Section
      id="b"
      kicker="B · Sink the hero"
      title="Ivory header, image starts below."
      note="Keep the current ivory header, push the hero image down. Simplest, most polished, still crops nothing."
    >
      <VariantSink />
    </Section>

    <Section
      id="c"
      kicker="C · Dark headroom"
      title="Extend the top of the image with a matching gradient."
      note="Add ~80px of dark gradient above the image so the header sits over that band. Header uses ivory text. Feels like the image was shot with more sky."
    >
      <VariantHeadroom />
    </Section>

    <div className="py-16 text-center hairline-t">
      <div className="serif italic text-2xl">Which fix do we lock in?</div>
      <div className="text-sm text-ink/60 mt-2">Tell me A, B, or C.</div>
      <Link to="/" className="crimson-link nav-item mt-4 inline-block">← Back to Home</Link>
    </div>
  </div>
);

export default HomeHeroPreview;
